import {
  CanonicalSchema,
  FieldMapping,
  Sample,
  TestCaseResult,
  TestRunResult,
  TestStepTrace,
  TestViolation,
} from './types';

const segments = (path: string): string[] => path.split('.').filter(Boolean);

export const getValue = (input: unknown, path: string): unknown =>
  segments(path).reduce<unknown>((value, key) => {
    if (value === null || value === undefined) return undefined;
    const index = Number(key);
    if (Array.isArray(value)) return Number.isInteger(index) ? value[index] : undefined;
    if (typeof value !== 'object') return undefined;
    return (value as Record<string, unknown>)[key];
  }, input);

export const setValue = (target: Record<string, unknown>, path: string, value: unknown): void => {
  const keys = segments(path);
  if (keys.length === 0) return;

  let cursor = target;
  keys.slice(0, -1).forEach((key) => {
    const next = cursor[key];
    if (next === null || typeof next !== 'object' || Array.isArray(next)) {
      cursor[key] = {};
    }
    cursor = cursor[key] as Record<string, unknown>;
  });
  cursor[keys[keys.length - 1]] = value;
};

const coerce = (raw: string): unknown => {
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  if (raw === 'null') return null;
  if (raw.trim() !== '' && Number.isFinite(Number(raw))) return Number(raw);
  return raw;
};

export const resolveMapping = (payload: unknown, mapping: FieldMapping): unknown => {
  if (mapping.sourceKind === 'constant') return coerce(mapping.source);
  if (mapping.sourceKind === 'field') return getValue(payload, mapping.source);

  return mapping.source.replace(/\{([^}]+)\}/g, (_match, path: string) => {
    const value = getValue(payload, path.trim());
    return value === undefined || value === null ? '' : String(value);
  });
};

export const applyMappings = (payload: unknown, mappings: FieldMapping[]): Record<string, unknown> => {
  const output: Record<string, unknown> = {};
  mappings.forEach((mapping) => {
    if (!mapping.targetPath) return;
    const value = resolveMapping(payload, mapping);
    if (value === undefined) return;
    setValue(output, mapping.targetPath, value);
  });

  return output;
};

const typeOf = (value: unknown): string => {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  return typeof value;
};

const checkValue = (value: unknown, field: CanonicalSchema['fields'][number], path: string): TestViolation[] => {
  if (value === undefined || value === null || value === '') {
    return field.required ? [{ path, message: 'Required field is not mapped' }] : [];
  }
  if (field.type === 'any') return [];
  const actual = typeOf(value);

  return actual === field.type ? [] : [{ path, message: `Expected ${field.type} but the mapping produced ${actual}` }];
};

export const validate = (output: Record<string, unknown>, schema?: CanonicalSchema): TestViolation[] => {
  if (!schema) return [];

  return schema.fields.flatMap<TestViolation>((field) => {
    /**
     * A typed line item is a contract on every element, so it is checked per element and named
     * with its index — `lines[0].qty` is what the runtime reports and what a person can act on.
     */
    if (field.path.includes('[].')) {
      const [arrayPath, itemPath] = field.path.split('[].');
      const array = getValue(output, arrayPath);
      // the array itself is reported by its own field, so its absence is not repeated here
      if (!Array.isArray(array)) return [];

      return array.flatMap((item, index) =>
        checkValue(getValue(item, itemPath), field, `${arrayPath}[${index}].${itemPath}`),
      );
    }

    return checkValue(getValue(output, field.path), field, field.path);
  });
};

export const unmappedRequiredFields = (mappings: FieldMapping[], schema?: CanonicalSchema): string[] => {
  if (!schema) return [];
  const mapped = new Set(mappings.filter((mapping) => mapping.targetPath).map((mapping) => mapping.targetPath));

  return schema.fields
    .filter(
      (field) =>
        field.required &&
        !mapped.has(field.path) &&
        !(field.path.includes('[].') && mapped.has(field.path.split('[].')[0])),
    )
    .map((field) => field.path);
};

const parsePayload = (sample: Sample): { payload?: unknown; error?: string } => {
  try {
    return { payload: JSON.parse(sample.payload) };
  } catch (error) {
    return { error: error instanceof Error ? error.message : 'Payload is not valid JSON' };
  }
};

export const runCase = (sample: Sample, mappings: FieldMapping[], schema?: CanonicalSchema): TestCaseResult => {
  const trace: TestStepTrace[] = [];
  const { payload, error } = parsePayload(sample);

  if (error !== undefined) {
    trace.push({ label: 'Parse vendor payload', detail: error, ok: false });
    return { sampleId: sample.id, sampleName: sample.name, ok: false, trace, violations: [] };
  }

  trace.push({ label: 'Parse vendor payload', detail: 'Payload parsed as JSON', ok: true });

  const output = applyMappings(payload, mappings);
  const applied = mappings.filter((mapping) => resolveMapping(payload, mapping) !== undefined).length;
  trace.push({
    label: 'Apply transformation',
    detail: `${applied} of ${mappings.length} mappings produced a value`,
    ok: applied > 0,
  });

  const violations = validate(output, schema);
  trace.push({
    label: 'Validate against canonical contract',
    detail: violations.length === 0 ? 'Output satisfies the canonical schema' : `${violations.length} violation(s)`,
    ok: violations.length === 0,
  });

  trace.push({ label: 'Deliver to sink', detail: 'Skipped. Test mode never contacts a sink.', ok: true });

  return {
    sampleId: sample.id,
    sampleName: sample.name,
    ok: violations.length === 0 && applied > 0,
    output,
    trace,
    violations,
  };
};

export const runTests = (
  routeId: string,
  scope: string,
  revisionId: string,
  samples: Sample[],
  mappings: FieldMapping[],
  schema?: CanonicalSchema,
): TestRunResult => {
  const cases = samples.map((sample) => runCase(sample, mappings, schema));

  return {
    routeId,
    scope,
    revisionId,
    ranAt: new Date().toISOString(),
    cases,
    passed: cases.filter((result) => result.ok).length,
    total: cases.length,
    sinkSuppressed: true,
  };
};

export const contentHash = (mappings: FieldMapping[], flow?: string): string => {
  const canonical = JSON.stringify([
    [...mappings]
      .filter((mapping) => mapping.targetPath)
      .sort((a, b) => a.targetPath.localeCompare(b.targetPath))
      .map(({ targetPath, sourceKind, source }) => [targetPath, sourceKind, source]),
    // the drawn flow is part of what runs, so it is part of what identifies the bundle
    flow ?? '',
  ]);

  let hash = 0;
  for (let index = 0; index < canonical.length; index++) {
    hash = (Math.imul(31, hash) + canonical.charCodeAt(index)) | 0;
  }

  return `bundle-${(hash >>> 0).toString(16).padStart(8, '0')}`;
};
